// Dashboard-side helpers + types for the DSL rule engine surface.
//
// Mirrors `dashboard/src/automod.ts`:
//   • exports the data shapes returned by the bot admin API
//   • clones / normalizes drafts so the editor can mutate freely without
//     trampling the server snapshot
//   • parses + serialises detail-template lists (one line per detail)
//
// Wire-format note: detail templates round-trip through the API as a string[]
// but the textarea surface uses one line per detail, so we trim + filter
// blanks here in one place rather than scattering it across the renderer.

export type RuleEventName =
  | "incidentRecorded"
  | "joinScored"
  | "raidDetected"
  | "threatLevelChanged"
  | "impersonationDetected"
  | "inviteAttributed"
  | "selfBotDetected";

export type RuleSeverity = "low" | "medium" | "high" | "critical";
export type RuleThreatLevel = "elevated" | "critical";
export type RuleActionKind = "incident" | "threatEscalation";

export interface RuleIncidentAction {
  type: "incident";
  severity: RuleSeverity;
  summaryTemplate: string;
  detailTemplates: string[];
}

export interface RuleThreatEscalationAction {
  type: "threatEscalation";
  level: RuleThreatLevel;
  reasonTemplate: string;
}

export type RuleAction = RuleIncidentAction | RuleThreatEscalationAction;

export interface RuleDefinition {
  id: string;
  name: string;
  enabled: boolean;
  event: RuleEventName;
  when: string;
  actions: RuleAction[];
  cooldownSeconds: number;
  createdAt: string;
  updatedAt: string;
}

export interface RuleFieldEntry {
  name: string;
  type: string;
  note?: string;
}

export interface RuleCatalog {
  events: RuleEventName[];
  severities: RuleSeverity[];
  threatLevels: RuleThreatLevel[];
  fields: Record<RuleEventName, ReadonlyArray<RuleFieldEntry>>;
  limits: {
    maxRulesPerGuild: number;
    maxNameLength: number;
    maxWhenLength: number;
    maxSummaryLength: number;
    maxDetailLength: number;
    maxReasonLength: number;
    maxDetailLines: number;
    maxCooldownSeconds: number;
  };
}

export interface RulesListResponse {
  rules: RuleDefinition[];
  catalog: RuleCatalog;
}

export interface RuleParseResponse {
  ok: boolean;
  error?: string;
}

/**
 * Editor-side draft. Most fields mirror `RuleDefinition`, but `actions` is
 * narrowed to a single action per rule for now — the bot supports an array,
 * but the UI is much simpler with one action and the wire format still
 * handles the multi-action case if we expand later.
 */
export interface RuleDraft {
  id: string | null;
  name: string;
  enabled: boolean;
  event: RuleEventName;
  when: string;
  cooldownSeconds: number;
  actionKind: RuleActionKind;
  severity: RuleSeverity;
  summaryTemplate: string;
  detailTemplatesText: string;
  threatLevel: RuleThreatLevel;
  reasonTemplate: string;
}

export const RULE_EVENT_LABELS: Record<RuleEventName, string> = {
  incidentRecorded: "Incident recorded",
  joinScored: "Join scored",
  raidDetected: "Raid detected",
  threatLevelChanged: "Threat level changed",
  impersonationDetected: "Impersonation detected",
  inviteAttributed: "Invite attributed",
  selfBotDetected: "Self-bot detected"
};

export const RULE_SEVERITY_LABELS: Record<RuleSeverity, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical"
};

export const RULE_THREAT_LABELS: Record<RuleThreatLevel, string> = {
  elevated: "Elevated",
  critical: "Critical"
};

export function createBlankRuleDraft(): RuleDraft {
  return {
    id: null,
    name: "",
    enabled: true,
    event: "incidentRecorded",
    when: "",
    cooldownSeconds: 0,
    actionKind: "incident",
    severity: "medium",
    summaryTemplate: "",
    detailTemplatesText: "",
    threatLevel: "elevated",
    reasonTemplate: ""
  };
}

export function ruleDraftFromDefinition(rule: RuleDefinition): RuleDraft {
  const incidentAction = rule.actions.find(
    (action): action is RuleIncidentAction => action.type === "incident"
  );
  const threatAction = rule.actions.find(
    (action): action is RuleThreatEscalationAction => action.type === "threatEscalation"
  );

  return {
    id: rule.id,
    name: rule.name,
    enabled: rule.enabled,
    event: rule.event,
    when: rule.when,
    cooldownSeconds: rule.cooldownSeconds,
    actionKind: incidentAction ? "incident" : "threatEscalation",
    severity: incidentAction?.severity ?? "medium",
    summaryTemplate: incidentAction?.summaryTemplate ?? "",
    detailTemplatesText: serialiseDetailTemplates(incidentAction?.detailTemplates ?? []),
    threatLevel: threatAction?.level ?? "elevated",
    reasonTemplate: threatAction?.reasonTemplate ?? ""
  };
}

export interface DraftValidationOk {
  ok: true;
  payload: {
    name: string;
    event: RuleEventName;
    when: string;
    enabled: boolean;
    cooldownSeconds: number;
    actions: RuleAction[];
  };
}

export interface DraftValidationErr {
  ok: false;
  error: string;
}

export type DraftValidationResult = DraftValidationOk | DraftValidationErr;

export function validateRuleDraft(
  draft: RuleDraft,
  catalog: RuleCatalog
): DraftValidationResult {
  const trimmedName = draft.name.trim();
  if (!trimmedName) {
    return { ok: false, error: "Name is required." };
  }
  if (trimmedName.length > catalog.limits.maxNameLength) {
    return { ok: false, error: `Name must be ${catalog.limits.maxNameLength} characters or fewer.` };
  }

  const trimmedWhen = draft.when.trim();
  if (!trimmedWhen) {
    return { ok: false, error: "Condition is required." };
  }
  if (trimmedWhen.length > catalog.limits.maxWhenLength) {
    return { ok: false, error: `Condition must be ${catalog.limits.maxWhenLength} characters or fewer.` };
  }

  if (draft.cooldownSeconds < 0 || draft.cooldownSeconds > catalog.limits.maxCooldownSeconds) {
    return {
      ok: false,
      error: `Cooldown must be between 0 and ${catalog.limits.maxCooldownSeconds} seconds.`
    };
  }

  let action: RuleAction;
  if (draft.actionKind === "incident") {
    const summary = draft.summaryTemplate.trim();
    if (!summary) {
      return { ok: false, error: "Summary template is required for incident actions." };
    }
    if (summary.length > catalog.limits.maxSummaryLength) {
      return {
        ok: false,
        error: `Summary must be ${catalog.limits.maxSummaryLength} characters or fewer.`
      };
    }
    const detailTemplates = parseDetailTemplates(draft.detailTemplatesText);
    if (detailTemplates.length > catalog.limits.maxDetailLines) {
      return {
        ok: false,
        error: `At most ${catalog.limits.maxDetailLines} detail lines are supported.`
      };
    }
    if (detailTemplates.some((line) => line.length > catalog.limits.maxDetailLength)) {
      return {
        ok: false,
        error: `Each detail line must be ${catalog.limits.maxDetailLength} characters or fewer.`
      };
    }

    action = {
      type: "incident",
      severity: draft.severity,
      summaryTemplate: summary,
      detailTemplates
    };
  } else {
    const reason = draft.reasonTemplate.trim();
    if (!reason) {
      return { ok: false, error: "Reason template is required for threat-escalation actions." };
    }
    if (reason.length > catalog.limits.maxReasonLength) {
      return {
        ok: false,
        error: `Reason must be ${catalog.limits.maxReasonLength} characters or fewer.`
      };
    }
    action = {
      type: "threatEscalation",
      level: draft.threatLevel,
      reasonTemplate: reason
    };
  }

  return {
    ok: true,
    payload: {
      name: trimmedName,
      event: draft.event,
      when: trimmedWhen,
      enabled: draft.enabled,
      cooldownSeconds: Math.trunc(draft.cooldownSeconds),
      actions: [action]
    }
  };
}

export function parseDetailTemplates(raw: string): string[] {
  return raw
    .split(/\r?\n/g)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export function serialiseDetailTemplates(values: string[]): string {
  return values.join("\n");
}

/** Pretty-print rule actions for the list view, matching the slash-command formatter. */
export function describeRuleAction(action: RuleAction): string {
  return action.type === "incident"
    ? `incident:${action.severity}`
    : `threat:${action.level}`;
}

/** Sort rules by `updatedAt` descending so the most recently touched float to the top. */
export function sortRulesByRecency(rules: RuleDefinition[]): RuleDefinition[] {
  return [...rules].sort(
    (left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt)
  );
}
